import React from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import DCAIcon from '../../../assets/icons/GenAi/dca-icon.svg';
import { ILsRagMessage, ILsRagSource } from '../../../libs/services/LifeSciencesRagService/LifeSciencesRagService';
import { renderMarkdown } from '../../../components/layout/HomePage/AppHeader/OverviewHeader/SkillsAssistant/renderMarkdown';

// yyyy-mm-dd-HHmm in local time, e.g. 2026-10-30-1432.
const timestampSlug = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const downloadAsMarkdown = (content: string, agentLabel: string) => {
  const slug = agentLabel.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${slug || 'agent-response'}-${timestampSlug()}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface IChatMessageProps {
  msg: ILsRagMessage;
  expandedSources: Set<string>;
  onToggleSource: (key: string) => void;
  showAgentTag?: boolean;
  // Drives the downloaded .md filename (e.g. "Migration Assistant" -> migration-assistant-2026-10-30-1432.md).
  agentLabel?: string;
}

const ChatMessage: React.FC<IChatMessageProps> = ({
  msg, expandedSources, onToggleSource, showAgentTag = false, agentLabel = 'agent',
}) => {
  const sourcesArr: ILsRagSource[] = Array.isArray(msg.sources) ? msg.sources as ILsRagSource[] : [];
  const isUser = msg.role === 'user';
  const topSources = Object.values(
    sourcesArr.reduce<Record<string, ILsRagSource>>((acc, src) => {
      const key = src.drug_name || src.document_name;
      if (!acc[key] || src.similarity > acc[key].similarity) acc[key] = src;
      return acc;
    }, {})
  ).sort((a, b) => b.similarity - a.similarity).slice(0, 4);

  return (
    <React.Fragment>
      <div className={`cmsg ${isUser ? 'cmsg-user' : 'cmsg-asst'}`}>
        {!isUser && <img src={DCAIcon} className="msg-av" alt="agent" />}
        <div className="cmsg-inner">
          <div className={`cbbl${isUser ? '' : ' cbbl--md'}`}>
            {isUser ? msg.content : renderMarkdown(msg.content)}
          </div>
          {!isUser && topSources.length > 0 && (
            <div className="msg-cites">
              <span className="cite-lbl">
                <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M3 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zm1 3h6v1H4V4zm0 2h6v1H4V6zm0 2h4v1H4V8z"/>
                </svg>
                Sources
              </span>
              {topSources.map((src, i) => {
                const srcKey = `${msg.id}-${i}`;
                const isOpen = expandedSources.has(srcKey);
                const hasExcerpt = !!src.excerpt;
                return (
                  <div key={i} className={`cite-item${isOpen ? ' open' : ''}`}>
                    <div
                      className="cite-row"
                      onClick={() => hasExcerpt && onToggleSource(srcKey)}
                      style={{ cursor: hasExcerpt ? 'pointer' : 'default' }}
                    >
                      <span className="cite-name">
                        {src.drug_name || src.document_name.replace(/_/g, ' ').replace('.pdf', '')}
                      </span>
                      <span className="cite-sim">{Math.round(src.similarity * 100)}%</span>
                      {hasExcerpt && (
                        <span className={`cite-chev${isOpen ? ' up' : ''}`}>
                          <svg width="9" height="9" viewBox="0 0 10 10" fill="currentColor">
                            <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                          </svg>
                        </span>
                      )}
                    </div>
                    {hasExcerpt && (
                      <div className={`cite-excerpt${isOpen ? ' open' : ''}`}>{src.excerpt}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {!isUser && msg.id === '__welcome__' && showAgentTag && (
            <span className="agt-tag">Scientific Literature FAQ</span>
          )}
          {!isUser && msg.id !== '__welcome__' && (msg.model || msg.tier || msg.cacheHit || !!msg.content) && (
            <div className="chat-bubble__tags">
              {msg.cacheHit && (
                <span className="chat-bubble__badge chat-bubble__badge--cache">Cached</span>
              )}
              {msg.tier && !msg.cacheHit && (
                <span className="chat-bubble__badge chat-bubble__badge--tier">{msg.tier}</span>
              )}
              {msg.model && (
                <span className="chat-bubble__badge chat-bubble__badge--model">{msg.model}</span>
              )}
              {typeof msg.cost === 'number' && (
                <span className="chat-bubble__badge chat-bubble__badge--cost">
                  {`$${msg.cost.toFixed(6)}`}
                </span>
              )}
              {!!msg.content && (
                <button
                  type="button"
                  className="chat-bubble__download-btn"
                  title="Download this output as a Markdown (.md) file"
                  onClick={() => downloadAsMarkdown(msg.content, agentLabel)}
                >
                  <DownloadOutlined />
                  {'Download Output'}
                </button>
              )}
            </div>
          )}
        </div>
        {isUser && <div className="msg-av av-sb">S</div>}
      </div>
    </React.Fragment>
  );
};

export default ChatMessage;
